/** Knowledge Hub review status helpers (shared frontend). */

export type ReviewStatus =
  | 'active'
  | 'needs-review'
  | 'review-requested'
  | 'draft'
  | 'archived'
  | string

/** Canonical KH frontmatter status values (bet: knowledge-hub-extension). */
export const KNOWLEDGE_HUB_STATUSES: ReviewStatus[] = [
  'draft',
  'review-requested',
  'needs-review',
  'active',
  'archived',
]

/** Allowed review_interval values from the KH frontmatter contract. */
export const REVIEW_INTERVAL_OPTIONS = [
  { value: '3months', label: '3 months' },
  { value: '6months', label: '6 months' },
  { value: '12months', label: '12 months' },
  { value: 'never', label: 'Never' },
] as const

export const REVIEW_STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-500/10 text-green-600 dark:text-green-400 border-green-500/20',
  'needs-review': 'bg-orange-500/10 text-orange-600 dark:text-orange-400 border-orange-500/20',
  'review-requested': 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border-blue-500/20',
  draft: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400 border-yellow-500/20',
  archived: 'bg-muted text-muted-foreground border-border',
}

/** Human-readable label for a review status slug. */
export function reviewStatusLabel(status: string | undefined): string {
  if (!status) return 'Unknown'
  return status.replace(/-/g, ' ')
}

/** Human-readable label for a review_interval slug (e.g. 12months -> 12 months). */
export function reviewIntervalLabel(interval: unknown): string {
  if (!interval) return 'Not set'
  const raw = String(interval)
  const known = REVIEW_INTERVAL_OPTIONS.find((o) => o.value === raw)
  if (known) return known.label
  const months = parseReviewIntervalMonths(raw)
  if (months != null) return `${months} months`
  return raw
}

/**
 * Derived next review date from verified_at + review_interval.
 * Returns null when interval is never/invalid or verified_at is missing.
 */
export function computeNextReviewDate(
  verifiedAt: string | undefined,
  reviewInterval: unknown
): Date | null {
  const months = parseReviewIntervalMonths(reviewInterval)
  if (!months || !verifiedAt) return null
  const verified = new Date(verifiedAt)
  if (Number.isNaN(verified.getTime())) return null
  const due = new Date(verified)
  due.setMonth(due.getMonth() + months)
  return due
}

/** True when frontmatter enables Knowledge Hub review UX for this document. */
export function isReviewEnabled(frontmatter: Record<string, unknown> | undefined): boolean {
  if (!frontmatter) return false
  return frontmatter.review_enabled === true || Boolean(frontmatter.review_interval)
}

/** Parse review_interval values like 12months; null means never or invalid. */
export function parseReviewIntervalMonths(interval: unknown): number | null {
  if (!interval || interval === 'never') return null
  const m = String(interval).match(/^(\d+)\s*months?$/i)
  return m ? parseInt(m[1], 10) : null
}

export type KhAcknowledgement = {
  by: string
  at: string
  verified_at: string
}

function normalizeKhEmail(email: string): string {
  return email.trim().toLowerCase()
}

/** Parse approvers/readers frontmatter to email strings. */
export function khEmailListFromFrontmatter(
  frontmatter: Record<string, unknown> | undefined,
  key: 'approvers' | 'readers'
): string[] {
  if (!frontmatter) return []
  const v = frontmatter[key]
  if (Array.isArray(v)) return v.filter(Boolean).map(String)
  if (typeof v === 'string' && v.trim()) return [v.trim()]
  return []
}

/**
 * Whether this user may mark the document verified.
 * Approval off: any member. Approval on: a listed approver only.
 * Matches evaluateMarkVerifiedGate in knowledge-hub-approvers.js.
 */
export function canUserMarkKhVerified(
  frontmatter: Record<string, unknown> | undefined,
  userEmail: string | Array<string | null | undefined> | null | undefined
): boolean {
  if (frontmatter?.approval_required !== true) return true
  const emails = (Array.isArray(userEmail) ? userEmail : [userEmail])
    .map((email) => (email ? normalizeKhEmail(email) : ''))
    .filter(Boolean)
  if (!emails.length) return false
  const approvers = khEmailListFromFrontmatter(frontmatter, 'approvers').map(normalizeKhEmail)
  return emails.some((email) => approvers.includes(email))
}

/** Readers with no acknowledgement for the current verified_at. */
export function countOutstandingKhReaders(
  frontmatter: Record<string, unknown> | undefined
): number {
  const readers = khEmailListFromFrontmatter(frontmatter, 'readers')
  if (!readers.length) return 0
  const verifiedAt =
    typeof frontmatter?.verified_at === 'string' ? frontmatter.verified_at : undefined
  if (!verifiedAt) return readers.length
  const acks = Array.isArray(frontmatter?.acknowledgements)
    ? (frontmatter.acknowledgements as KhAcknowledgement[])
    : []
  const acked = new Set(
    acks
      .filter((a) => a?.verified_at === verifiedAt)
      .map((a) => normalizeKhEmail(String(a.by || '')))
  )
  return readers.filter((r) => !acked.has(normalizeKhEmail(r))).length
}

/** Whether userEmail acknowledged the current verified_at. */
export function hasKhAcknowledgedCurrentVerify(
  frontmatter: Record<string, unknown> | undefined,
  userEmail: string | undefined
): boolean {
  const verifiedAt =
    typeof frontmatter?.verified_at === 'string' ? frontmatter.verified_at : undefined
  if (!verifiedAt || !userEmail) return false
  const acks = Array.isArray(frontmatter?.acknowledgements)
    ? (frontmatter.acknowledgements as KhAcknowledgement[])
    : []
  const norm = normalizeKhEmail(userEmail)
  return acks.some(
    (a) => a?.verified_at === verifiedAt && normalizeKhEmail(String(a.by || '')) === norm
  )
}

/** Parse textarea (newline/comma) into email list for frontmatter. */
export function parseKhEmailListInput(raw: string): string[] {
  return raw
    .split(/[\n,;]+/)
    .map((s) => s.trim())
    .filter(Boolean)
}

export function formatKhEmailListInput(emails: string[]): string {
  return emails.join('\n')
}

/** True when verified_at plus review_interval is before today. */
export function isReviewOverdue(verifiedAt: string | undefined, reviewInterval: unknown): boolean {
  const months = parseReviewIntervalMonths(reviewInterval)
  if (!months || !verifiedAt) return false
  const verified = new Date(verifiedAt)
  if (Number.isNaN(verified.getTime())) return false
  const due = new Date(verified)
  due.setMonth(due.getMonth() + months)
  return due < new Date()
}

export type ReviewChangelogEntry = {
  at: string
  by: string
  action: string
  note?: string
}

const FENCE_RE = /```yaml(?:\s+review-changelog)?\s*\r?\n([\s\S]*?)\r?\n```/

/** Lightweight client-side fence parse (body only, no yaml lib). */
/** Lightweight client-side parse of review-changelog fence entries from document body. */
export function parseReviewChangelogFence(body: string): ReviewChangelogEntry[] {
  const m = body.match(FENCE_RE)
  if (!m) return []
  const entries: ReviewChangelogEntry[] = []
  const block = m[1]
  const entryBlocks = block.split(/\n\s*-\s+at:/).filter(Boolean)
  for (const chunk of entryBlocks) {
    const text = chunk.startsWith('at:') ? chunk : `at:${chunk}`
    const atMatch = text.match(/at:\s*['"]?([^'"\n]+)/)
    const byMatch = text.match(/by:\s*['"]?([^'"\n]+)/)
    const actionMatch = text.match(/action:\s*['"]?([^'"\n]+)/)
    const noteMatch = text.match(/note:\s*['"]?([^'"\n]+)/)
    if (atMatch && byMatch && actionMatch) {
      entries.push({
        at: atMatch[1].trim(),
        by: byMatch[1].trim(),
        action: actionMatch[1].trim(),
        note: noteMatch?.[1]?.trim(),
      })
    }
  }
  return entries
}
